<script setup lang="ts">
import { Activiteit, ActiviteitKenmerk } from "@/repositories/activiteit.repository";
import { computed, onMounted, reactive, watch } from "vue";
import { nl as nlLocale } from "date-fns/locale"
import { getFormattedTime } from "@/helpers/timezone.helper";
import WidgetDetailsLessen from "./WidgetDetailsLessen.vue";
import { Kenmerk, KenmerkenList } from "@/repositories/kenmerk.repository";

const props = defineProps<{
    activiteit: Activiteit
    kenmerken: KenmerkenList
}>()

const emit = defineEmits<{
    (e: "open-boeken", activiteit: Activiteit): void;
}>()

const openBoeken = (activiteit: Activiteit) => {
    emit("open-boeken", activiteit)
}

const steps = [0, 1, 2, 3, 4];

const getStarClass = (step: number, rating: number) => {
    const roundedScore = Math.round(rating * 10);
    var calcStep = step * 10;
    return roundedScore > calcStep ? (roundedScore < (calcStep + 10) ? "half" : "full") : "empty";
}

const getNaamFromList = (kenmerk: number, list: any[]) => {
    var filteredTags = list.filter(function (value) { return value.Id === kenmerk; });

    return filteredTags.length ? filteredTags[0].Naam : "";
};


const filterItems = (activiteitKenmerken: ActiviteitKenmerk[], kenmerken: Kenmerk[]) => {
    return activiteitKenmerken?.filter((item: ActiviteitKenmerk) => {
        return kenmerken.some((kenmerk: Kenmerk) => { return kenmerk.Id == item.Kenmerk; });
    }) || [];
}
</script>

<template>
    <div class="sportmatch-widget-panel-details">
        <div class="sportmatch-widget-panel-details-header">

            <img :src="activiteit.AfbeeldingUrl + '?width=320&height=320&mode=crop'"
                class="sportmatch-widget-panel-details-header-logo" :alt="activiteit.SportaanbiederItem.Naam">
                
            <button class="btn btn-secondary" @click="openBoeken(activiteit)">
                Reserveer nu
            </button>
            <h2 class="h4 sportmatch-widget-panel-details-header-title">
                {{ activiteit.Naam }}
            </h2>
            <p class="sportmatch-widget-panel-details-header-subtitle">Aangeboden door: <strong>{{
                activiteit.SportaanbiederItem.Naam }}</strong></p>
            <div class="sportmatch-widget-panel-details-header-rating">
                <ul class="sportmatch-widget-panel-details-header-rating-stars">
                    <li v-for="step in steps" :key="step"
                        :class="getStarClass(step, activiteit.SportaanbiederItem.ReviewScore)"><span
                            class="icon icon-star-active"></span>
                        <span class="icon icon-star-half"></span>
                        <span class="icon icon-star-empty"></span>
                    </li>
                </ul>
                <p class="sportmatch-widget-panel-details-header-rating-review-count">({{
                    activiteit.SportaanbiederItem.ReviewCount }} reviews)</p>
            </div>
            <ul class="sportmatch-widget-panel-details-header-activity-details">
                <li>
                    <div>
                        <span class="icon icon-agenda" aria-hidden="true"></span>{{ getFormattedTime(activiteit.BeginDatum, 'dd-MM-yyyy', nlLocale) }}
                    </div>
                    <div>
                        <span class="icon icon-clock" aria-hidden="true"></span>{{ getFormattedTime(activiteit.BeginDatum, 'HH:mm', nlLocale) }} - {{ getFormattedTime(activiteit.EindDatum, 'HH:mm', nlLocale) }}uur
                    </div>
                </li>
                <li>
                    <div>
                        <span class="icon icon-map-pin"></span>{{ activiteit.LocatieItem.Naam }}, {{ activiteit.LocatieItem.Adres }}, {{ activiteit.LocatieItem.Woonplaats }}
                    </div>
                </li>
                <li v-if="activiteit.BetalingsType == 'Credits'"><div><span class="icon icon-euro"></span>1 credit</div></li>
                <li v-if="activiteit.BetalingsType == 'Gratis'"><div><span class="icon icon-euro"></span>Gratis</div></li>
                <li v-if="activiteit.BetalingsType == 'Betaling'"><div><span class="icon icon-euro"></span>{{activiteit.Kosten }}</div></li>
            </ul>
        </div>

        <div class="sportmatch-widget-panel-details-content">
            <h2 class="h4 tab-content-title">Informatie</h2>
            <p class="information-content">{{ activiteit.Beschrijving }}</p>
            <table class="table table-responsive table-condensed-unstyled activity-table mb-2 mb-md-4">
                <tbody>
                    <tr v-if="activiteit.SportenTranslated?.length">
                        <td class="table-category">Sport:</td>
                        <td class="table-content">
                            <ul class="list-inline">
                                <li v-for="sport in activiteit.SportenTranslated" :key="'sport-' + sport">
                                    <span class="small-badge">{{ sport }}</span>
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr v-if="activiteit.CultuuractiviteitenTranslated?.length">
                        <td class="table-category">Activiteit:</td>
                        <td class="table-content">
                            <ul class="list-inline">
                                <li v-for="cultuuractiviteit in activiteit.CultuuractiviteitenTranslated"
                                    :key="'activiteit-' + cultuuractiviteit">
                                    <span class="small-badge">{{ cultuuractiviteit }}</span>
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr v-if="activiteit.Sportmomenttype != null">
                        <td class="table-category">Type:</td>
                        <td class="table-content">{{ getNaamFromList(activiteit.Sportmomenttype, kenmerken.types) }}
                        </td>
                    </tr>
                    <tr v-if="activiteit.MinLeeftijd != null || activiteit.MaxLeeftijd != null">
                        <td class="table-category">Leeftijden:</td>
                        <td class="table-content"><span>{{ activiteit.MinLeeftijd }} t/m {{ activiteit.MaxLeeftijd
                                }}
                                jaar</span></td>
                    </tr>
                    <tr v-if="activiteit.FysiekeInspanning?.length">
                        <td class="table-category">Inspanning:</td>
                        <td class="table-content">
                            <ul class="list-inline">
                                <li v-for="kenmerk in activiteit.FysiekeInspanning" :key="'inspanning-' + kenmerk">
                                    <span class="small-badge">{{ getNaamFromList(kenmerk.Kenmerk,
                                        kenmerken.inspanningList)
                                        }}</span>
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr v-if="activiteit.Doelgroep?.length">
                        <td class="table-category">Doelgroep:</td>
                        <td class="table-content">
                            <ul class="list-inline">
                                <li v-for="kenmerk in activiteit.Doelgroep" :key="'doelgroep-' + kenmerk">
                                    <span class="small-badge">{{ getNaamFromList(kenmerk.Kenmerk,
                                        kenmerken.doelgroepList)
                                        }}</span>
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr v-if="activiteit.Voordeel?.length">
                        <td class="table-category">Voordelen:</td>
                        <td class="table-content">
                            <ul class="list-inline">
                                <li v-for="kenmerk in activiteit.Voordeel" :key="'voordeel-' + kenmerk">
                                    <span class="small-badge">{{ getNaamFromList(kenmerk.Kenmerk,
                                        kenmerken.voordeelList)
                                        }}</span>
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr v-if="activiteit.Tag?.length">
                        <td class="table-category">Tags:</td>
                        <td class="table-content">
                            <ul class="list-inline">
                                <li v-for="kenmerk in activiteit.Tag" :key="'tag-' + kenmerk">
                                    <span class="small-badge">{{ getNaamFromList(kenmerk.Kenmerk, kenmerken.tagList)
                                        }}</span>
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr v-if="activiteit.SpecifiekAanbod?.length">
                        <td class="table-category">Aangepast aanbod:</td>
                        <td class="table-content">
                            <ul class="list-inline">
                                <li class="small-badge" v-for="kenmerk in activiteit.SpecifiekAanbod"
                                    :key="'aanbod-' + kenmerk">
                                    <span class="small-badge">{{ getNaamFromList(kenmerk.Kenmerk,
                                        kenmerken.specifiekAanbodList) }}</span>
                                </li>
                            </ul>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div v-if="activiteit.ExtraKosten" class="my-2 d-flex flex-column">
                <div class="font-weight-bold">
                    <span class="icon icon-alert-circle text-primary mr-1 font-weight-bold"></span> Er moet bijbetaald
                    worden voor deze activiteit
                </div>
                <div>
                    {{ activiteit.ExtraKostenBeschrijving }}
                </div>
            </div>

            <div class="facilities-container mb-2 mb-md-4"
                v-if="filterItems(activiteit.Inclusief, kenmerken.inclusiefList)?.length || filterItems(activiteit.AanbodInclusief, kenmerken.aanbodInclusiefList)?.length">
                <h2 class="h4 tab-content-title">Inclusief</h2>
                <ul class="facility-list">
                    <li v-for="item in filterItems(activiteit.Inclusief, kenmerken.inclusiefList)"
                        :key="'inclusief-' + item.Kenmerk">
                        <span class="icon icon-oval"></span>{{ getNaamFromList(item.Kenmerk, kenmerken.inclusiefList) }}
                    </li>
                    <li v-for="item in filterItems(activiteit.AanbodInclusief, kenmerken.aanbodInclusiefList)"
                        :key="'aanbod-inclusied-' + item.Kenmerk">
                        <span class="icon icon-oval"></span>{{ getNaamFromList(item.Kenmerk,
                            kenmerken.aanbodInclusiefList) }}
                    </li>
                </ul>
            </div>

            <h2 class="h4 tab-content-title">Wat je niet mag vergeten</h2>
            <p class="information-content mb-2 mb-md-4">
                {{ activiteit.ExtraInformatie }}
            </p>
            <h2 class="h4 tab-content-title" v-if="activiteit.Lessen?.length > 0">Lessen</h2>

            <WidgetDetailsLessen v-if="activiteit.Lessen?.length > 0" :lessen="activiteit.Lessen"></WidgetDetailsLessen>
            <button class="btn btn-secondary" @click="openBoeken(activiteit)">
                Reserveer nu
            </button>
        </div>
    </div>
</template>